/**
 * useCatalog.ts — catalog state machine hook
 *
 * Cycle 5 change: in vertical-ref mode, fetches catalog from
 * GET /api/broker/v1/services (DB-sourced, JWT-authenticated) instead of
 * reading static tiles from config.json.
 *
 * Token (fix 2026-08-02): the fetch obtains a fresh token via getToken()
 * when provided — the context `token` stays in the deps so a background
 * refresh re-runs the fetch, but the request itself is never built around
 * a possibly-stale value.
 *
 * WP-SL-V3 addition: visibility-tier + claims filtering (SL-R-027, ADR-040)
 *   - public  → visible to all authenticated users
 *   - private → visible only to users with admin-level realm roles
 *   - coming-soon → always visible (muted, not actionable)
 * Non-entitled private tiles are excluded from the result entirely
 * (DoD: "non-entitled tiles absent").
 *
 * Admin roles that grant access to private tiles:
 *   tenant-admin, service-admin, vertical-product-owner
 * (broker-operator dropped 2026-08-02 — resolved STALE, it matched nobody.)
 *
 * All other modes continue using config.catalog.tiles (existing behaviour).
 *
 * WP-S1-PUBLISH-d — the tenant store renders ENTITLED tiles only:
 *   The vertical-tenant store's catalogue IS the ENTITLEMENT-FILTERED response of
 *   GET /v1/services (fetched here as /api/broker/v1/services). Entitlement is
 *   decided SERVER-SIDE: the endpoint returns exactly the services the caller is
 *   entitled to, and this hook renders that list — an unentitled app arrives
 *   ABSENT from the response, never as a hidden-then-disabled tile. The
 *   visibility-tier filter below is a SEPARATE, narrower gate (private vs public
 *   presentation) layered on top of the entitled set; it never widens it.
 *   The real server-side entitlement filter lands with WP-S1-PUBLISH c; until
 *   then hooks/servicesFixture.ts stands in for the entitled response in tests,
 *   with the same ApiService contract this converter reads.
 *
 * Ref: cycle 5 brief §3.3, design-contract §11, WP-SL-V3 brief, WP-S1-PUBLISH-d
 */

import { useEffect, useReducer, useRef, useState } from 'react';
import type { TileConfig, InstanceMode } from '../config/schema';
import { isAuthenticatedBroker } from '../config/schema';

export type CatalogState =
  | { tag: 'loading' }
  | { tag: 'loaded'; tiles: TileConfig[] }
  | { tag: 'empty-no-results'; activeFilter: string }
  | { tag: 'empty-no-access' }
  | { tag: 'empty-fetch-failed'; errorCode?: string };

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; tiles: TileConfig[] }
  | { type: 'FETCH_ERROR'; errorCode?: string }
  | { type: 'FILTER_EMPTY'; activeFilter: string }
  | { type: 'NO_ACCESS' };

const SKELETON_MIN_MS = 240;

/**
 * Realm roles that grant VISIBILITY of private-tier services.
 *
 * ⚠ This set is the client mirror of the SERVER's CATALOG_ADMIN_ROLES
 * (services.ts) and it is deliberately NOT the operator set. The asymmetry is
 * policy-2026-08-01's, restated by
 * decision-2026-08-02-tenant-admin-scope-is-registry-mutation: SEEING IS NOT
 * TOUCHING. `tenant-admin` belongs here and must stay — removing it to "align"
 * this list with the operator predicates would hide private tiles from tenant
 * admins, which is the 2026-08-02 defect reintroduced from the other side.
 * For the same reason this set is NOT consolidated into auth/surfaceAccess.ts:
 * that file holds one predicate per navigable SURFACE, and this is not one.
 *
 * 2026-08-02: broker-operator removed as the fourth and last site of the
 * phantom role (open-task-2026-08-02-broker-operator-role-absent-in-realm,
 * resolved STALE). That removal is a no-op for every real identity — nobody
 * has ever held the role — and it leaves the tenant-admin asymmetry intact.
 *
 * 2026-08-03 (decision-2026-08-03-role-model-planes): service-admin and
 * vertical-product-owner collapse into `broker-admin`. This set is the SPA
 * mirror of the server's CATALOG_ADMIN_ROLES (infraops routes/services.ts,
 * default `broker-admin,tenant-admin`) — the two change together.
 */
const PRIVATE_ALLOWED_ROLES = new Set([
  'tenant-admin',
  'broker-admin',
]);

function reducer(_state: CatalogState, action: Action): CatalogState {
  switch (action.type) {
    case 'FETCH_START':    return { tag: 'loading' };
    case 'FETCH_SUCCESS':  return { tag: 'loaded', tiles: action.tiles };
    case 'FETCH_ERROR':    return { tag: 'empty-fetch-failed', errorCode: action.errorCode };
    case 'FILTER_EMPTY':   return { tag: 'empty-no-results', activeFilter: action.activeFilter };
    case 'NO_ACCESS':      return { tag: 'empty-no-access' };
    default:               return _state;
  }
}

// Shape returned by GET /api/broker/v1/services
interface ApiService {
  id: string;
  name: string;
  description: string | null;
  vertical: string;
  /** Registry tenant, null where the column/row carries none. See TileConfig.tenant. */
  tenant?: string | null;
  mode: string;
  status: string;
  mark: string | null;
  tags: string[];
  action: string;
  instances: unknown[];
  deployment?: {
    phase: 'uat' | 'published';
    deploy_no: number;
    deploy_date: string;
    release_tag?: string | null;
  } | null;
}

export type { ApiService };

/**
 * Exported for the App Operations Board: the board needs the app's own
 * catalogue row (title, description, deployment) to render App details and to
 * hand a TileConfig to DeployRolloutModal. It reads the SAME endpoint through
 * the SAME converter — a second projection of a service would be a second
 * answer to what a service is.
 */
export function apiServiceToTile(s: ApiService): TileConfig {
  return {
    id: s.id,
    title: s.name,
    description: s.description ?? '',
    mark: s.mark ?? '',
    action: (s.action as TileConfig['action']) ?? 'launch',
    vertical: s.vertical,
    tenant: s.tenant ?? null,
    status: (s.status as TileConfig['status']) ?? 'public',
    tags: s.tags ?? [],
    instances: [],
    // href intentionally omitted: binding flow provides redirect_uri at runtime
    ...(s.deployment ? {
      deployment: {
        phase: s.deployment.phase,
        deployNo: s.deployment.deploy_no,
        deployDate: s.deployment.deploy_date,
        releaseTag: s.deployment.release_tag ?? undefined,
      },
    } : {}),
  };
}

/**
 * Filter a service list by the user's realm roles (visibility-tier gate).
 * - 'public' and 'coming-soon' pass for all authenticated users
 * - 'private' passes only if the user holds an admin-level role
 */
function applyVisibilityFilter(tiles: TileConfig[], userRoles: string[]): TileConfig[] {
  const hasAdminRole = userRoles.some((r) => PRIVATE_ALLOWED_ROLES.has(r));
  // A private tile carrying an active UAT deployment was already authorised
  // for this caller SERVER-SIDE (ADR-013 groups predicate, ADR-075 B3) —
  // the client filter must not hide it from approvers/testers.
  return tiles.filter((t) =>
    t.status !== 'private' || hasAdminRole || t.deployment?.phase === 'uat');
}

interface UseCatalogOptions {
  tiles: TileConfig[];
  activeCategory: string;
  search: string;
  mode?: InstanceMode;
  token?: string | null;
  /** Fresh-token source (fix 2026-08-02) — preferred over `token` for the fetch itself. */
  getToken?: () => Promise<string | null>;
  userRoles?: string[];
  /** Preview override — skip fetch, force a state */
  preview?: CatalogState['tag'];
}

export function useCatalog({ tiles, activeCategory, search, mode, token, getToken, userRoles = [], preview }: UseCatalogOptions) {
  const skeletonStart = useRef<number>(0);
  const [state, dispatch] = useReducer(reducer, { tag: 'loading' });

  // retry() used to dispatch FETCH_START and nothing else. That set the state
  // to 'loading' while the fetch — which lives in the effect below — was never
  // re-issued, because none of the effect's deps had changed. The Retry button
  // on the error card therefore replaced the error with a skeleton that never
  // resolved, and it hid the diagnosis on 2026-08-02: the page made zero
  // further requests no matter how often it was pressed. A nonce in the deps
  // is what makes retry mean retry.
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (preview) {
      switch (preview) {
        case 'loading':
          dispatch({ type: 'FETCH_START' }); return;
        case 'loaded':
          dispatch({ type: 'FETCH_SUCCESS', tiles }); return;
        case 'empty-no-results':
          dispatch({ type: 'FILTER_EMPTY', activeFilter: 'preview' }); return;
        case 'empty-no-access':
          dispatch({ type: 'NO_ACCESS' }); return;
        case 'empty-fetch-failed':
          dispatch({ type: 'FETCH_ERROR', errorCode: 'PREVIEW_ERROR' }); return;
      }
    }

    dispatch({ type: 'FETCH_START' });
    skeletonStart.current = Date.now();

    // Authenticated broker modes (meta, tenant, vertical): DB-sourced catalog from the API
    if (mode && isAuthenticatedBroker(mode) && (getToken || token)) {
      let cancelled = false;
      (async () => {
        try {
          const freshToken = getToken ? await getToken() : token;
          if (cancelled) return;
          if (!freshToken) {
            dispatch({ type: 'NO_ACCESS' }); return;
          }
          // cache: 'no-store' is not a performance choice, it is a correctness one.
          //
          // Until 2026-07-31 the dev.mtcm.do.place vhost answered /api/* with a
          // blanket 308 PERMANENT redirect to another origin. A browser that saw
          // it cached the redirect, and 308s are not retractable: for two days
          // that browser sent this call straight to the other origin without ever
          // contacting the server, so no server-side fix could reach it.
          //
          // Worse, the redirect was CROSS-ORIGIN, and browsers strip the
          // Authorization header across origins. The request therefore arrived
          // unauthenticated and 401'd in under a millisecond with no JWT error
          // logged — which also rules out healing this at the redirect target:
          // proxying and CORS cannot restore a credential the browser removed.
          //
          // The only fix that reaches an already-poisoned client is to stop
          // consulting the HTTP cache, where the redirect lives. Every broker
          // API call in this SPA is authenticated and dynamic, so none of them
          // should have been cacheable in the first place.
          const res = await fetch('/api/broker/v1/services', {
            cache: 'no-store',
            headers: { 'Authorization': `Bearer ${freshToken}` },
          });
          if (cancelled) return;
          if (res.status === 401) {
            dispatch({ type: 'NO_ACCESS' }); return;
          }
          if (!res.ok) {
            dispatch({ type: 'FETCH_ERROR', errorCode: String(res.status) }); return;
          }
          const { services } = await res.json() as { services: ApiService[] };
          if (cancelled) return;

          // Convert, apply visibility-tier filter, then category/search
          let filtered = applyVisibilityFilter(services.map(apiServiceToTile), userRoles);
          if (activeCategory !== 'All') {
            filtered = filtered.filter((t) => t.tags.includes(activeCategory));
          }
          if (search.trim()) {
            const q = search.toLowerCase();
            filtered = filtered.filter(
              (t) => t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q),
            );
          }

          const elapsed = Date.now() - skeletonStart.current;
          const delay = Math.max(0, SKELETON_MIN_MS - elapsed);
          setTimeout(() => {
            if (cancelled) return;
            if (filtered.length === 0 && services.length > 0) {
              dispatch({ type: 'FILTER_EMPTY', activeFilter: activeCategory || search });
            } else if (services.length === 0) {
              dispatch({ type: 'NO_ACCESS' });
            } else {
              dispatch({ type: 'FETCH_SUCCESS', tiles: filtered });
            }
          }, delay);
        } catch (err) {
          // Surface WHAT threw, not just that something did. A CORS-blocked
          // cross-origin redirect, a DNS failure, an offline tab and a JSON
          // parse error all arrive here as one thrown fetch, and collapsing
          // them into the string "NETWORK_ERROR" cost hours on 2026-08-02 —
          // the real cause was a 308 the browser had cached two days earlier
          // and was never sending to the server at all. The code line under
          // the error card is the one place a user can read this, so put the
          // thrown name and message there.
          const detail = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
          if (!cancelled) {
            dispatch({ type: 'FETCH_ERROR', errorCode: `NETWORK_ERROR — ${detail}`.slice(0, 160) });
          }
        }
      })();
      return () => { cancelled = true; };
    }

    // All other modes: client-side filter on static tiles from config (unchanged)
    const delay = Math.max(0, SKELETON_MIN_MS - (Date.now() - skeletonStart.current));
    const timer = setTimeout(() => {
      let filtered = tiles;
      if (activeCategory !== 'All') {
        filtered = filtered.filter((t) => t.tags.includes(activeCategory));
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        filtered = filtered.filter(
          (t) => t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q),
        );
      }
      if (filtered.length === 0 && tiles.length > 0) {
        dispatch({ type: 'FILTER_EMPTY', activeFilter: activeCategory || search });
      } else if (tiles.length === 0) {
        dispatch({ type: 'NO_ACCESS' });
      } else {
        dispatch({ type: 'FETCH_SUCCESS', tiles: filtered });
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [tiles, activeCategory, search, mode, token, getToken, userRoles, preview, retryNonce]);

  // Bumping the nonce re-runs the effect, which dispatches FETCH_START itself
  // and then actually re-fetches.
  return { state, retry: () => setRetryNonce((n) => n + 1) };
}
